import { useApi }                from '../../utils/api'
import { useTranslation }        from 'react-i18next'
import React, {
  useContext,
  useState
}                                from 'react'
import BaseTable, { BaseSearch } from '../../components/base-table'
import {
  Box, Button,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
}                             from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import {
  dateFromInputStringToSql, dateToInputString,
  dateToUserString,
}                             from '../../utils/dateutils'
import Iconify                from '../../components/Iconify'
import Page                   from '../../components/Page'
import {
  MessageDeliveryStatus,
  MessageProcessingStatus,
  User
}                             from '../../consts'
import { DateTime }           from 'luxon'
import { AppContext }         from "../../App"

export default function MessagesPage () {

  const api = useApi()
  const { t } = useTranslation()
  const [query, setQuery] = useState("")
  const [processingStatus, setProcessingStatus] = useState("")
  const [deliveryStatus, setDeliveryStatus] = useState("")
  const [dateTimeFrom, setDateTimeFrom] = useState(dateToInputString(DateTime.now().minus({ minutes: 10 }).toISO()))
  const [dateTimeTo, setDateTimeTo] = useState(dateToInputString(DateTime.now().plus({ minutes: 10 }).toISO()))
  const [showFilter, setShowFilter] = useState(true);
  const [forceReloadCounter, setForceReloadCounter] = useState(0);
  const { user } = useContext(AppContext)

  const columns = [
    { field: 'id', headerName: t('ID'), width: 70, sortable: false, filterable: false, renderCell: (cell) => (
        <RouterLink
          to={`/dashboard/messages/${cell.row.id}`}
        >
          {cell.row.id}
        </RouterLink>
      ), },
    { field: 'created', headerName: t('Created'), width: 170, sortable: false, filterable: false, renderCell: (cell) => {
      return (<Typography color={'color.secondary'}>{dateToUserString(cell.row.created)}</Typography>)
    }},
    { field: 'processingStatus', headerName: t('Processing status'), width: 120, sortable: false, filterable: false, renderCell: (cell) => {
      return t(cell.row.processingStatus ?? MessageProcessingStatus.scheduled);
      } },
    { field: 'deliveryStatus', headerName: t('Delivery status'), width: 120, sortable: false, filterable: false, renderCell: (cell) => {
      return cell.row.deliveryStatus ? t(cell.row.deliveryStatus) : '---';
      } },
    { field: 'telFrom', headerName: t('Tel. number'), width: 190, sortable: false, filterable: false, renderCell: (cell) => {
      return (<Stack direction={'row'} spacing={2} alignItems={'center'}>
        <Typography variant={'string'} color={'color.secondary'}>{cell.row.telFrom}</Typography>
        <Iconify icon={'material-symbols:arrow-forward'} />
        <Typography variant={'string'} color={'color.secondary'}>{cell.row.telTo}</Typography>
      </Stack>);
      } },
    { field: 'content', headerName: t('Content'), flex: 1, sortable: false, filterable: false },
  ];

  const statusField = (id, label, value, setValue, statuses) => (<>
    <FormControl sx={{  minWidth: 150 }}>
      <InputLabel id={`label-${id}`}>{label}</InputLabel>
      <Select
        labelId={`label-${id}`}
        value={value}
        onChange={(e) => {
          setValue(e.target.value)
        }}
        label={label}
      >
        <MenuItem value="">
          <em>{t('All')}</em>
        </MenuItem>
        {
          Object.values(statuses).map((status) => {
            return <MenuItem key={status} value={status}>{t(status)}</MenuItem>
          })
        }
      </Select>
    </FormControl>
  </>)

  const onSubmitSearch = () => {
    setForceReloadCounter(forceReloadCounter + 1);
  }

  return (<Page title={t('Messages')}>
    <Container>
      <Stack direction="row" alignItems="center" mb={1} spacing={2}>
        <Typography variant="h4" gutterBottom>{t('Messages')}</Typography>
        <Box flexGrow={1} />
        <IconButton onClick={(e) => {
          setShowFilter(!showFilter)
        }} >
          <Iconify icon={showFilter ? 'eva:funnel-fill' : 'eva:funnel-outline'} />
        </IconButton>
      </Stack>
      {showFilter && <>
        <Stack direction={'row'} justifyContent={'space-between'} mb={5} spacing={2}
          component={'form'} onSubmit={(e) => {
            e.preventDefault();
            onSubmitSearch();
          }}>
          <TextField
            id="datetime-local"
            label={t('From')}
            type="datetime-local"
            value={dateTimeFrom}
            onChange={(e) => {
              setDateTimeFrom(e.target.value)
            }}
            sx={{ minWidth: 250 }}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            id="datetime-local"
            label={t('To')}
            type="datetime-local"
            value={dateTimeTo}
            onChange={(e) => {
              setDateTimeTo(e.target.value)
            }}
            sx={{ minWidth: 250 }}
            InputLabelProps={{
              shrink: true,
            }}
          />
          {statusField('processing-status', t('Processing status'), processingStatus, setProcessingStatus, MessageProcessingStatus)}
          {statusField('delivery-status', t('Delivery status'), deliveryStatus, setDeliveryStatus, MessageDeliveryStatus)}
          <BaseSearch query={query} onQueryChanged={(query) => setQuery(query)} />
          <Button type={'submit'} variant="outlined">{t('Search')}</Button>
        </Stack>
      </>}
      <BaseTable
        columns={columns}
        query={query}
        forceReloadCounter={forceReloadCounter}
        loadCallback={async (query, page, pageSize) => {
          try {
            const params = {
              from: dateTimeFrom ? dateFromInputStringToSql(dateTimeFrom) : null,
              to: dateTimeTo ? dateFromInputStringToSql(dateTimeTo) : null,
              processingStatus: processingStatus,
              deliveryStatus: deliveryStatus,
              ...(/^(\+421|09)\d{8,9}$/.test(query) ? { telNumber: query } : { query: query }),
            }
            return await (user.type === User.typeViewer ? api.viewer.message.list : api.message.list)(params, page, pageSize)
          } catch (e) {
            console.log('Failed to load messages', e)
            return {
              data: [],
              totalCount: 0,
            }
          }
        }} />
    </Container>
  </Page>)

}